import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  RemoveCircle as RemoveCircleIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon
} from '@mui/icons-material';

import { 
  getEmailGroupsByTag, 
  getTags, 
  assignEmailNameToTag,
  removeEmailNameFromTag,
  removeAllEmailsFromTag,
  createTag,
  updateTag,
  deleteTag,
  EmailGroup,
  Tag,
  getEmailsForAIAnalysis,
  analyzeEmailWithAI,
  analyzeMultipleEmailsWithAI,
  createTagFromAIAnalysis,
  assignExistingTagToEmail,
  EmailForAIAnalysis,
  AITagSuggestion,
  AIAnalysisResult
} from '../../services/tagService';
import LoadingState from '../../components/LoadingState';
import PageContainer from '../../components/PageContainer';
import apiService from '../../services/apiService';
import { DateRange, DateRangeType } from '../../services/analyticsService';
import { getSegmentColor } from '../../utils/segmentColors';

interface EmailGroupRow {
  email_name: string;
  client_count: number;
  clients: string[];
  client_ids: number[];
  first_email_id: number;
  email_ids: number[];  // All email IDs for this group
  total_opens: number;
  total_clicks: number;
  open_rate: number;
  click_rate: number;
  tags: Tag[];
  selected: boolean;
}

interface EmailDetail {
  id: number;
  name: string;
  subject: string;
  html: string;
  scheduled_date: string;
  segment_name: string;
  client_name: string;
}

const EmailSuppliers: React.FC<{ dateRange: DateRange; onDateRangeChange: (newDateRange: DateRange) => void; }> = ({ dateRange, onDateRangeChange }) => {
    const [loading, setLoading] = useState(false);
  const [emailGroups, setEmailGroups] = useState<EmailGroupRow[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // AI-related state
  const [aiEmails, setAiEmails] = useState<EmailForAIAnalysis[]>([]);
  const [aiAnalysisResults, setAiAnalysisResults] = useState<Record<string, AIAnalysisResult>>({});
  const [analyzingEmails, setAnalyzingEmails] = useState<Set<string>>(new Set());
  const [selectedAiEmails, setSelectedAiEmails] = useState<Set<string>>(new Set());
  
  // Tag action loading state
  const [processingTags, setProcessingTags] = useState<Set<string>>(new Set());
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmailDetail, setSelectedEmailDetail] = useState<EmailDetail | null>(null);
  const [loadingEmailDetail, setLoadingEmailDetail] = useState(false);

  // Individual tag assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedEmailName, setSelectedEmailName] = useState<string>('');
  const [selectedTagId, setSelectedTagId] = useState<number | ''>('');
  const [assigning, setAssigning] = useState(false);
  
  // Tag management drawer state
  const [tagDrawerOpen, setTagDrawerOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);

  useEffect(() => {
    console.log('Date range changed, fetching data:', dateRange);
    fetchData();
    fetchAIEmails();
  }, [dateRange.date_range]);

  const fetchTags = async () => {
    try {
      const tagsData = await getTags();
      setTags(tagsData);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching data with date range:', dateRange);
      
      const [groupsData, tagsData] = await Promise.all([
        getEmailGroupsByTag(dateRange.date_range),
        getTags()
      ]);
      
      console.log('Fetched email groups:', groupsData);
      console.log('Fetched tags:', tagsData);
      
      if (!groupsData || groupsData.length === 0) {
        console.log('No email groups returned from API');
        // Check if we should try a different date range
        if (dateRange.date_range === 'last_30_days') {
          console.log('Trying last 90 days instead...');
          const groupsData90 = await getEmailGroupsByTag('last_90_days');
          console.log('Fetched email groups (90 days):', groupsData90);
          if (groupsData90 && groupsData90.length > 0) {
            console.log('Found email groups with 90-day range');
            const groupsWithSelection = groupsData90.map((group: EmailGroup) => ({
              ...group,
              selected: false
            }));
            setEmailGroups(groupsWithSelection);
            setTags(tagsData);
            return;
          }
        }
      }
      
      const groupsWithSelection = groupsData.map((group: EmailGroup) => ({
        ...group,
        selected: false
      }));
      
      console.log('Processed email groups:', groupsWithSelection);
      
      setEmailGroups(groupsWithSelection);
      setTags(tagsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          response: (error as any).response?.data,
          status: (error as any).response?.status,
          statusText: (error as any).response?.statusText
        });
      }
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAssignTag = (emailName: string) => {
    // Check if there are available tags to assign
    const emailGroup = emailGroups.find(group => group.email_name === emailName);
    const availableTags = tags.filter(tag => 
      !emailGroup?.tags.some(t => t.id === tag.id)
    );
    
    if (availableTags.length === 0) {
      showSnackbar('All tags are already assigned to this email', 'warning');
      return;
    }
    
    setSelectedEmailName(emailName);
    setSelectedTagId('');
    setAssignDialogOpen(true);
  };

  const handleRemoveTag = (emailName: string, tagId: number) => {
    // Handle individual tag removal
    handleRemoveConfirm(emailName, tagId);
  };

  const handleAssignConfirm = async () => {
    if (!selectedTagId || !selectedEmailName) return;
    
    try {
      setAssigning(true);
      
      // Find the email group and get the stored email IDs
      const emailGroup = emailGroups.find(group => group.email_name === selectedEmailName);
      if (!emailGroup) {
        throw new Error('Email group not found');
      }
      
      // Check if tag is already assigned
      if (emailGroup.tags.some(t => t.id === selectedTagId)) {
        showSnackbar('This tag is already assigned to this email', 'warning');
        setAssignDialogOpen(false);
        setSelectedEmailName('');
        setSelectedTagId('');
        return;
      }
      
      // Find the tag object to add to the group
      const tagToAdd = tags.find(t => t.id === selectedTagId);
      if (!tagToAdd) {
        throw new Error('Tag not found');
      }
      
      console.log('Assigning tag to emails:', emailGroup.email_ids);
      
      // Assign tag to all emails with this name (single API call)
      await assignEmailNameToTag(selectedTagId as number, selectedEmailName);
      
      // Optimistic update: Update local state immediately
      const updatedEmailGroups = emailGroups.map(group => 
        group.email_name === selectedEmailName 
          ? { 
              ...group, 
              tags: [...group.tags, tagToAdd]
            }
          : group
      );
      setEmailGroups(updatedEmailGroups);
      
      showSnackbar(`Successfully assigned tag to ${selectedEmailName}`, 'success');
      setAssignDialogOpen(false);
      setSelectedEmailName('');
      setSelectedTagId('');

      // No need to call fetchData() - data is already updated locally
      
    } catch (error: any) {
      console.error('Error assigning supplier:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        stack: error.stack
      });
      
      let errorMessage = 'Error assigning tag';
      
      // Handle specific error cases
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.includes('already assigned') || detail.includes('already exists')) {
          errorMessage = 'This tag is already assigned to this email';
          // Refresh data to ensure UI is in sync
          fetchData();
        } else {
          errorMessage += `: ${detail}`;
        }
      } else if (error.response?.statusText) {
        errorMessage += `: ${error.response.statusText}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      } else if (error.response?.status) {
        errorMessage += ` (Status: ${error.response.status})`;
      } else {
        errorMessage += `: ${JSON.stringify(error)}`;
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveConfirm = async (emailName: string, tagId: number) => {
    // Find the email group and get the stored email IDs
    const emailGroup = emailGroups.find(group => group.email_name === emailName);
    if (!emailGroup) {
      showSnackbar('Email group not found', 'error');
      return;
    }
    
    // Store original tags for potential rollback
    const originalTags = [...emailGroup.tags];
    
    try {
      // Optimistic update: Remove tag from local state immediately
      const updatedEmailGroups = emailGroups.map(group => 
        group.email_name === emailName 
          ? { 
              ...group, 
              tags: group.tags.filter(t => t.id !== tagId)
            }
          : group
      );
      setEmailGroups(updatedEmailGroups);
      
      // Remove tag from all emails
      await removeEmailNameFromTag(tagId, emailName);
      
      showSnackbar(`Successfully removed tag from ${emailName}`, 'success');
      // No need to call fetchData() - data is already updated locally
      
    } catch (error) {
      console.error('Error removing tag:', error);
      
      // Revert optimistic update on error
      const revertedEmailGroups = emailGroups.map(group => 
        group.email_name === emailName 
          ? { 
              ...group, 
              tags: originalTags // Restore original tags
            }
          : group
      );
      setEmailGroups(revertedEmailGroups);
      
      showSnackbar('Error removing tag', 'error');
    }
  };

  // Tag management functions
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    
    // Create temporary tag for optimistic update
    const tempTag = { 
      id: Date.now(), // Temporary ID for optimistic update
      name: newTagName.trim(),
      email_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    try {
      // Optimistic update: Add tag to local state immediately
      setTags(prev => [...prev, tempTag]);
      setNewTagName('');
      
      // Make API call
      const newTag = await createTag({ name: newTagName.trim() });
      
      // Replace temporary tag with real one
      setTags(prev => prev.map(t => 
        t.id === tempTag.id ? newTag : t
      ));
      
      showSnackbar('Tag created successfully', 'success');
    } catch (error) {
      console.error('Error creating tag:', error);
      
      // Revert optimistic update on error
      setTags(prev => prev.filter(t => t.id !== tempTag.id));
      setNewTagName(newTagName.trim()); // Restore the name
      
      showSnackbar('Error creating tag', 'error');
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !editTagName.trim()) return;
    
    // Store original name for potential rollback
    const originalName = editingTag.name;
    
    try {
      // Optimistic update: Update tag name in local state immediately
      setTags(prev => prev.map(t => 
        t.id === editingTag.id 
          ? { ...t, name: editTagName.trim() }
          : t
      ));
      
      // Also update tag names in emailGroups state
      setEmailGroups(prev => prev.map(group => ({
        ...group,
        tags: group.tags.map(t => 
          t.id === editingTag.id 
            ? { ...t, name: editTagName.trim() }
            : t
        )
      })));
      
      // Close edit dialog
      setEditingTag(null);
      setEditTagName('');
      
      // Make API call
      const updatedTag = await updateTag(editingTag.id, { name: editTagName.trim() });
      
      // Update with real data from server
      setTags(prev => prev.map(t => 
        t.id === updatedTag.id ? updatedTag : t
      ));
      
      showSnackbar('Tag updated successfully', 'success');
    } catch (error) {
      console.error('Error updating tag:', error);
      
      // Revert optimistic update on error
      setTags(prev => prev.map(t => 
        t.id === editingTag.id 
          ? { ...t, name: originalName }
          : t
      ));
      
      // Also revert emailGroups state
      setEmailGroups(prev => prev.map(group => ({
        ...group,
        tags: group.tags.map(t => 
          t.id === editingTag.id 
            ? { ...t, name: originalName }
            : t
        )
      })));
      
      // Reopen edit dialog with original name
      setEditTagName(originalName);
      
      showSnackbar('Error updating tag', 'error');
    }
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;
    
    // Store tag for potential rollback
    const tagToRestore = { ...tagToDelete };
    
    try {
      // Optimistic update: Remove tag from local state immediately
      setTags(prev => prev.filter(t => t.id !== tagToDelete.id));
      
      // Also remove tag from all emailGroups
      setEmailGroups(prev => prev.map(group => ({
        ...group,
        tags: group.tags.filter(t => t.id !== tagToDelete.id)
      })));
      
      setDeleteDialogOpen(false);
      setTagToDelete(null);
      
      // Delete the tag (backend will automatically remove assignments)
      await deleteTag(tagToDelete.id);
      
      showSnackbar('Tag and all its assignments deleted successfully', 'success');
      // No need to update state again - already removed optimistically
      
    } catch (error) {
      console.error('Error deleting tag:', error);
      
      // Revert optimistic update on error
      setTags(prev => [...prev, tagToRestore]);
      
      // Also revert emailGroups state
      setEmailGroups(prev => prev.map(group => ({
        ...group,
        tags: group.tags.some(t => t.id === tagToRestore.id) 
          ? group.tags 
          : [...group.tags, tagToRestore]
      })));
      
      setDeleteDialogOpen(false);
      setTagToDelete(null);
      
      showSnackbar('Error deleting tag', 'error');
    }
  };

  const openEditDialog = (tag: Tag) => {
    setEditingTag(tag);
    setEditTagName(tag.name);
  };

  const openDeleteDialog = (tag: Tag) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  // Helper function to determine if a tag actually exists in the database
  const isTagActuallyExisting = (tagName: string): boolean => {
    return tags.some(tag => tag.name === tagName);
  };

  // AI functionality
  const fetchAIEmails = async () => {
    try {
      const emailsData = await getEmailsForAIAnalysis(dateRange.date_range);
      setAiEmails(emailsData);
    } catch (error) {
      console.error('Error fetching AI emails:', error);
      showSnackbar('Error loading emails for AI analysis', 'error');
    }
  };

  const analyzeSingleEmail = async (emailName: string) => {
    try {
      setAnalyzingEmails(prev => new Set(prev).add(emailName));
      
      const analysis = await analyzeEmailWithAI(emailName);
      
      console.log('AI Analysis result:', analysis);
      console.log('Analysis result structure:', analysis.analysis_result);
      
      setAiAnalysisResults(prev => ({
        ...prev,
        [emailName]: analysis.analysis_result
      }));
      
      showSnackbar('Email analyzed successfully', 'success');
    } catch (error) {
      console.error('Error analyzing email:', error);
      showSnackbar('Error analyzing email', 'error');
    } finally {
      setAnalyzingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(emailName);
        return newSet;
      });
    }
  };

  const analyzeSelectedEmails = async () => {
    if (selectedAiEmails.size === 0) {
      showSnackbar('Please select emails to analyze', 'error');
      return;
    }

    try {
      const emailNames = Array.from(selectedAiEmails);
      
      // Mark selected emails as analyzing
      setAnalyzingEmails(prev => new Set([...prev, ...emailNames]));

      // Analyze each email individually
      for (const emailName of emailNames) {
        try {
          const analysis = await analyzeEmailWithAI(emailName);
          
          setAiAnalysisResults(prev => ({
            ...prev,
            [emailName]: analysis.analysis_result
          }));
        } catch (error) {
          console.error(`Error analyzing email ${emailName}:`, error);
        }
      }

      showSnackbar(`Analyzed ${emailNames.length} emails successfully`, 'success');
    } catch (error) {
      console.error('Error analyzing emails:', error);
      showSnackbar('Error analyzing emails', 'error');
    } finally {
      setAnalyzingEmails(prev => new Set());
    }
  };

  const handleTagAction = async (emailName: string, tag: AITagSuggestion, action: 'accept' | 'reject') => {
    if (action === 'reject') {
      // Remove the suggested tag from results
      setAiAnalysisResults(prev => {
        const result = prev[emailName];
        if (result) {
          return {
            ...prev,
            [emailName]: {
              ...result,
              tags: result.tags.filter(t => t.name !== tag.name)
            }
          };
        }
        return prev;
      });
      return;
    }

    // Create a unique key for this tag action
    const tagActionKey = `${emailName}-${tag.name}`;
    
    try {
      // Set loading state for this specific tag action
      setProcessingTags(prev => new Set(prev).add(tagActionKey));
      
      // Determine if this tag actually exists in the database
      const isActuallyExisting = isTagActuallyExisting(tag.name);
      
      if (isActuallyExisting) {
        // Find the existing tag by name
        const existingTag = tags.find(t => t.name === tag.name);
        
        if (existingTag) {
          // Assign existing tag
          const result = await assignExistingTagToEmail(existingTag.id, emailName);
          
          if (result.success) {
            showSnackbar(`Assigned ${tag.name} to all emails with name "${emailName}"`, 'success');
            
            // Update UI optimistically without screen flash
            setEmailGroups(prev => prev.map(group => {
              if (group.email_name === emailName) {
                if (existingTag && !group.tags?.some(t => t.id === existingTag.id)) {
                  return {
                    ...group,
                    tags: [...(group.tags || []), existingTag]
                  };
                }
              }
              return group;
            }));
            
            // Remove the accepted tag from AI suggestions
            setAiAnalysisResults(prev => {
              const result = prev[emailName];
              if (result) {
                return {
                  ...prev,
                  [emailName]: {
                    ...result,
                    tags: result.tags.filter(t => t.name !== tag.name)
                  }
                };
              }
              return prev;
            });
          }
        }
      } else {
        // Create new tag
        const result = await createTagFromAIAnalysis(tag.name, emailName);
        
        if (result.success) {
          showSnackbar(`Created tag ${tag.name} and assigned to all emails with name "${emailName}"`, 'success');
          
          // Update UI optimistically with the new tag
          const newTag: Tag = {
            id: result.tag_id || 0,
            name: tag.name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          setEmailGroups(prev => prev.map(group => {
            if (group.email_name === emailName) {
              return {
                ...group,
                tags: [...(group.tags || []), newTag]
              };
            }
            return group;
          }));
          
          // Remove the accepted tag from AI suggestions
          setAiAnalysisResults(prev => {
            const result = prev[emailName];
            if (result) {
              return {
                ...prev,
                [emailName]: {
                  ...result,
                  tags: result.tags.filter(t => t.name !== tag.name)
                }
              };
            }
            return prev;
          });
          
          // Refresh tags list to include the new tag
          fetchTags();
        }
      }
    } catch (error) {
      console.error('Error handling tag action:', error);
      showSnackbar('Error handling tag action', 'error');
    } finally {
      // Clear loading state for this tag action
      setProcessingTags(prev => {
        const newSet = new Set(prev);
        newSet.delete(tagActionKey);
        return newSet;
      });
    }
  };

  const handleEmailSelection = (emailName: string, selected: boolean) => {
    setSelectedAiEmails(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(emailName);
      } else {
        newSet.delete(emailName);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedAiEmails(new Set(aiEmails.map(email => email.email_name)));
    } else {
      setSelectedAiEmails(new Set());
    }
  };

  const getFirstEmailId = (emailGroup: EmailGroupRow): number | null => {
    return emailGroup.first_email_id || null;
  };

  const handleEmailClick = async (emailId: number, emailName: string) => {
    try {
      setLoadingEmailDetail(true);
      setModalOpen(true);
      
      const response = await apiService.get<EmailDetail>(`/api/emails/${emailId}`);
      setSelectedEmailDetail(response.data);
    } catch (error) {
      console.error('Error fetching email detail:', error);
      showSnackbar('Error loading email details', 'error');
      setModalOpen(false);
    } finally {
      setLoadingEmailDetail(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEmailDetail(null);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState message="Loading email groups..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header with Date Range Selector - Always Visible */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box className="flex items-center gap-2">
          <Typography variant="body2" className="text-gray-700 whitespace-nowrap">
            Date Range:
          </Typography>
          <FormControl size="small" className="min-w-[180px]">
            <Select
              value={dateRange.date_range}
              onChange={(e) => onDateRangeChange({ date_range: e.target.value as DateRangeType })}
              displayEmpty
              className="bg-white"
            >
              <MenuItem value="last_7_days">Last 7 Days</MenuItem>
              <MenuItem value="last_30_days">Last 30 Days</MenuItem>
              <MenuItem value="last_90_days">Last 90 Days</MenuItem>
              <MenuItem value="last_12_months">Last 12 Months</MenuItem>
            </Select>
          </FormControl>
          {dateRange.startDate && dateRange.endDate && (
            <Typography variant="body2" className="text-gray-500">
              ({dateRange.startDate.toLocaleDateString()} - {dateRange.endDate.toLocaleDateString()})
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          onClick={() => setTagDrawerOpen(true)}
          startIcon={<AssignmentIcon />}
        >
          Manage Tags
        </Button>
      </Box>
      


      {/* AI Scan Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="primary">
          AI-Assisted Tag Assignment
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAIEmails}
            size="small"
          >
            Refresh AI Emails
          </Button>
                    {selectedAiEmails.size > 0 && (
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={analyzeSelectedEmails}
              disabled={Array.from(analyzingEmails).some(email => selectedAiEmails.has(email))}
            >
              Scan All ({selectedAiEmails.size})
            </Button>
                          )}
        </Box>
      </Box>

      {/* Email Groups Table */}
      {emailGroups.length > 0 ? (
        <>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedAiEmails.size > 0 && selectedAiEmails.size === emailGroups.length}
                      indeterminate={selectedAiEmails.size > 0 && selectedAiEmails.size < emailGroups.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '30px' }}>Clients</TableCell>
                  <TableCell sx={{ width: '50%' }}>Email Name</TableCell>
                  <TableCell sx={{ width: '1fr' }}>Tags</TableCell>
                  <TableCell sx={{ width: '120px' }}>AI Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailGroups.map((group) => (
                  <TableRow key={group.email_name} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedAiEmails.has(group.email_name)}
                        onChange={(e) => handleEmailSelection(group.email_name, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: '30px' }}>
                      <Tooltip title={group.clients.join(', ')}>
                        <span>{group.client_count}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: '50%' }}>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={() => {
                          const firstEmailId = getFirstEmailId(group);
                          if (firstEmailId) {
                            handleEmailClick(firstEmailId, group.email_name);
                          }
                        }}
                      >
                        {group.email_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '1fr' }}>
                      <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center" justifyContent="space-between">
                        <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                          {group.tags && group.tags.length > 0 ? (
                            group.tags.map((tag) => (
                              <Box key={tag.id} display="flex" alignItems="center" gap={0.5}>
                                <Chip
                                  label={tag.name}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  onDelete={() => handleRemoveTag(group.email_name, tag.id)}
                                />
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No tags assigned
                            </Typography>
                          )}
                          
                          {/* AI Suggested Tags */}
                          {(() => {
                            const suggestedTags = aiAnalysisResults[group.email_name]?.tags || [];
                            // Filter out tags that already exist in the group
                            const filteredTags = suggestedTags.filter(tag => 
                              !group.tags?.some(existingTag => existingTag.name === tag.name)
                            );
                            
                            if (filteredTags.length === 0) return null;
                            
                            return (
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                                <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
                                  AI Suggested:
                                </Typography>
                                {filteredTags.map((tag) => {
                                  const tagActionKey = `${group.email_name}-${tag.name}`;
                                  const isProcessing = processingTags.has(tagActionKey);
                                  
                                  return (
                                    <Chip
                                      key={tag.name}
                                      label={tag.name}
                                      size="small"
                                      color={isTagActuallyExisting(tag.name) ? "default" : "error"}
                                      variant="outlined"
                                      sx={{ 
                                        '& .MuiChip-label': { 
                                          fontSize: '0.75rem',
                                          px: 1 
                                        } 
                                      }}
                                      deleteIcon={
                                        isProcessing ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <Box display="flex" gap={0.5}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleTagAction(group.email_name, tag, 'accept')}
                                              sx={{ p: 0, minWidth: 'auto' }}
                                              disabled={isProcessing}
                                            >
                                              <CheckIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleTagAction(group.email_name, tag, 'reject')}
                                              sx={{ p: 0, minWidth: 'auto' }}
                                              disabled={isProcessing}
                                            >
                                              <CloseIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        )
                                      }
                                      onDelete={() => {}} // Prevent default delete behavior
                                    />
                                  );
                                })}
                              </Box>
                            );
                          })()}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Tooltip title="Add Tag">
                            <IconButton
                              size="small"
                              onClick={() => handleAssignTag(group.email_name)}
                              disabled={(() => {
                                const availableTags = tags.filter(tag => 
                                  !group.tags?.some(t => t.id === tag.id)
                                );
                                return availableTags.length === 0;
                              })()}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '120px' }}>
                      <Box display="flex" gap={1}>
                        {analyzingEmails.has(group.email_name) ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Tooltip title="AI Scan">
                            <IconButton
                              size="small"
                              onClick={() => analyzeSingleEmail(group.email_name)}
                              color="primary"
                            >
                              <AutoAwesomeIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Alert severity="info">
          <Typography variant="body1" gutterBottom>
            No email groups found for the selected date range.
          </Typography>
          <Typography variant="body2">
            This could mean:
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>No emails were sent in the selected time period</li>
            <li>All emails in this period were filtered out (test segments, bounces, etc.)</li>
            <li>The database connection has issues</li>
          </ul>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Try selecting a different date range (e.g., "Last 90 Days" or "Last 12 Months") or check the browser console for more details.
          </Typography>
        </Alert>
      )}

      {/* Email Detail Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedEmailDetail?.name || 'Email Details'}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
          {loadingEmailDetail ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedEmailDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 3 }}>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
                <Typography variant="body1">{selectedEmailDetail.subject}</Typography>
              </Box>
              <Box mb={2} display="flex" gap={2} alignItems="center">
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Segment</Typography>
                  <Chip 
                    label={selectedEmailDetail.segment_name.charAt(0).toUpperCase() + selectedEmailDetail.segment_name.slice(1)}
                    size="small"
                    sx={{
                      backgroundColor: getSegmentColor(selectedEmailDetail.segment_name),
                      color: 'white',
                      mt: 0.5,
                      minWidth: '80px',
                      '& .MuiChip-label': {
                        px: 1,
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'visible'
                      }
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Scheduled Date</Typography>
                  <Typography variant="body1">
                    {selectedEmailDetail.scheduled_date ? new Date(selectedEmailDetail.scheduled_date).toLocaleString() : 'Not scheduled'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ 
                flex: 1,
                overflow: 'auto',
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 2, 
                bgcolor: 'grey.50'
              }}
              dangerouslySetInnerHTML={{ __html: selectedEmailDetail.html || 'No content available' }}
              />
            </Box>
          ) : (
            <Typography>No email details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Tag Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Tag</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a tag to assign to "{selectedEmailName}":
          </Typography>
          
          {/* Show currently assigned tags */}
          {selectedEmailName && (() => {
            const emailGroup = emailGroups.find(group => group.email_name === selectedEmailName);
            const assignedTags = emailGroup?.tags || [];
            
            // Debug info
            if (process.env.NODE_ENV === 'development') {
              console.log('Selected email name:', selectedEmailName);
              console.log('Found email group:', emailGroup);
              console.log('Assigned tags:', assignedTags);
            }
            
            return assignedTags.length > 0 ? (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Currently assigned tags:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {assignedTags.map(tag => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            ) : null;
          })()}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Tag</InputLabel>
            <Select
              value={selectedTagId}
              onChange={(e) => setSelectedTagId(e.target.value as number)}
              label="Select Tag"
            >
              {tags
                .filter(tag => {
                  // Filter out tags that are already assigned
                  const emailGroup = emailGroups.find(group => group.email_name === selectedEmailName);
                  return !emailGroup?.tags.some(t => t.id === tag.id);
                })
                .map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          
          {(() => {
            const emailGroup = emailGroups.find(group => group.email_name === selectedEmailName);
            const availableTags = tags.filter(tag => 
              !emailGroup?.tags.some(t => t.id === tag.id)
            );
            return availableTags.length === 0 ? (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                All tags are already assigned to this email.
              </Typography>
            ) : null;
          })()}

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignConfirm} 
            variant="contained" 
            disabled={!selectedTagId || assigning}
          >
            {assigning ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Supplier Management Drawer */}
      <Drawer
        anchor="right"
        open={tagDrawerOpen}
        onClose={() => setTagDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            p: 3,
          },
        }}
      >
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Tag Management</Typography>
            <IconButton onClick={() => setTagDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Add New Tag */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Add New Tag
            </Typography>
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Tag name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
              />
              <Button
                variant="contained"
                onClick={handleCreateTag}
                disabled={!newTagName.trim()}
              >
                Add
              </Button>
            </Box>
          </Paper>

          {/* Tag List */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Existing Tags
            </Typography>
            <List>
              {tags
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((tag) => (
                <ListItem key={tag.id} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">{tag.name}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(tag)}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    secondary={`${tag.email_count || 0} email${tag.email_count === 1 ? '' : 's'} associated`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => openDeleteDialog(tag)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      </Drawer>

      {/* Edit Tag Dialog */}
      <Dialog open={!!editingTag} onClose={() => setEditingTag(null)}>
        <DialogTitle>Edit Tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tag Name"
            fullWidth
            value={editTagName}
            onChange={(e) => setEditTagName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleUpdateTag()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTag(null)}>Cancel</Button>
          <Button onClick={handleUpdateTag} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{tagToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTag} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default EmailSuppliers; 